import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { LayoutGrid, RefreshCw, ShieldCheck } from 'lucide-react';
import { targetAPI } from '../../lib/api';
import { toast } from 'sonner';

const normalizeRoleId = (role) => (role?.id || role?.name || '').toString();

export function RoleTemplateMatrixPanel({ roles = [], sessions = [], templates = [], onRefresh }) {
  const [savingKey, setSavingKey] = useState(null);

  const standardSessions = useMemo(
    () => sessions.filter((session) => session?.tab_type !== 'custom' && session?.key),
    [sessions],
  );

  const orderedRoles = useMemo(() => {
    const hiddenRoles = new Set(['sales_admin']);
    const priority = ['admin', 'ceo', 'sales_director', 'product_director', 'finance_manager', 'marketing_manager', 'sales_rep', 'user'];
    return roles
      .filter((role) => !hiddenRoles.has(normalizeRoleId(role)))
      .sort((a, b) => priority.indexOf(normalizeRoleId(a)) - priority.indexOf(normalizeRoleId(b)));
  }, [roles]);

  const templatesBySession = useMemo(() => {
    return standardSessions.reduce((acc, session) => {
      acc[session.key] = templates.filter((template) => template.session_key === session.key);
      return acc;
    }, {});
  }, [standardSessions, templates]);

  const getAssignedTemplate = (roleId, sessionKey) => {
    return (templatesBySession[sessionKey] || []).find((template) => (template.assigned_roles || []).includes(roleId));
  };

  const buildTemplatePayload = (template, assignedRoles) => ({
    name: template.name,
    description: template.description || '',
    cards: template.cards || [],
    layout: template.layout || 'grid',
    session_key: template.session_key || null,
    assigned_roles: assignedRoles,
    is_default: !!template.is_default,
  });

  const handleAssign = async (roleId, sessionKey, nextTemplateId) => {
    const cellKey = `${roleId}-${sessionKey}`;
    setSavingKey(cellKey);
    try {
      const sessionTemplates = templatesBySession[sessionKey] || [];
      const updates = [];

      sessionTemplates.forEach((template) => {
        const nextRoles = new Set(template.assigned_roles || []);
        let changed = false;

        if (nextRoles.has(roleId) && template.id !== nextTemplateId) {
          nextRoles.delete(roleId);
          changed = true;
        }

        if (nextTemplateId !== '__fallback__' && template.id === nextTemplateId && !nextRoles.has(roleId)) {
          nextRoles.add(roleId);
          changed = true;
        }

        if (changed) {
          updates.push(targetAPI.updateTemplate(template.id, buildTemplatePayload(template, Array.from(nextRoles))));
        }
      });

      await Promise.all(updates);
      toast.success(`Updated ${roleId.replace(/_/g, ' ')} → ${sessionKey.toUpperCase()} assignment`);
      await onRefresh?.();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update role-to-tab assignment');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Card data-testid="role-template-matrix-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-[#800000]" />
            Role × Tab Matrix
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Quickly assign the active template for each role inside CRM, Finance, and Performance without editing every template one-by-one.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="role-matrix-refresh-button">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {standardSessions.map((session) => (
            <Badge key={session.key} variant="outline" className="text-xs">
              {session.name}: {(templatesBySession[session.key] || []).length} templates
            </Badge>
          ))}
        </div>
        <Separator />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] border-separate border-spacing-y-2" data-testid="role-template-matrix-table">
            <thead>
              <tr>
                <th className="text-left text-xs font-semibold text-muted-foreground px-3 py-2">Role</th>
                {standardSessions.map((session) => (
                  <th key={session.key} className="text-left text-xs font-semibold text-muted-foreground px-3 py-2">
                    {session.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orderedRoles.map((role) => {
                const roleId = normalizeRoleId(role);
                const roleName = role.name || roleId;
                return (
                  <tr key={roleId} data-testid={`role-matrix-row-${roleId}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="rounded-xl border bg-slate-50 px-3 py-2 min-w-[180px]">
                        <p className="text-sm font-semibold text-slate-800">{roleName}</p>
                        <p className="text-[11px] text-muted-foreground">{roleId}</p>
                      </div>
                    </td>
                    {standardSessions.map((session) => {
                      const assignedTemplate = getAssignedTemplate(roleId, session.key);
                      const cellKey = `${roleId}-${session.key}`;
                      const sessionTemplates = templatesBySession[session.key] || [];
                      return (
                        <td key={cellKey} className="px-3 py-2 align-top">
                          <div className="rounded-xl border bg-white px-3 py-3 min-w-[220px] space-y-2" data-testid={`role-matrix-cell-${cellKey}`}>
                            <Select
                              value={assignedTemplate?.id || '__fallback__'}
                              onValueChange={(value) => handleAssign(roleId, session.key, value)}
                              disabled={savingKey === cellKey}
                            >
                              <SelectTrigger data-testid={`role-matrix-select-${cellKey}`}>
                                <SelectValue placeholder="Use fallback template" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__fallback__">Use tab fallback template</SelectItem>
                                {sessionTemplates.map((template) => (
                                  <SelectItem key={template.id} value={template.id}>
                                    {template.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="flex flex-wrap gap-1.5">
                              <Badge variant="outline" className="text-[10px] h-5">
                                {(assignedTemplate?.cards || []).length || 0} cards
                              </Badge>
                              {assignedTemplate?.is_default && (
                                <Badge className="bg-[#800000]/10 text-[#800000] text-[10px] h-5">Default</Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground min-h-[30px]">
                              {assignedTemplate?.description || 'No explicit role template assigned — tab fallback will be used.'}
                            </p>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="rounded-xl border border-dashed bg-slate-50 px-4 py-3 text-xs text-muted-foreground flex items-start gap-2">
          <LayoutGrid className="h-4 w-4 mt-0.5 text-[#800000]" />
          <span>
            Changing a cell removes that role from other templates in the same tab and assigns it to the selected template, keeping the role-to-tab mapping clean.
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
